import { useMutation } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box } from "@mui/material";
import { GridActionsCellItem, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { TextCellWithTooltip } from "components/DataGridCell";
import { IssueCount } from "components/IssueCount";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { gql } from "generated-graphql";
import {
  ProductsWithMeasurementsQuery,
  ProductsWithMeasurementsQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useQueryParams } from "hooks/useQueryParams";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatChemicalName } from "util/chemicalName";

import { useFacility } from "../Facility/useFacility";
import { FacilityProductMeasurementForm } from "./FacilityProductMeasurementForm";
import { DeleteProductMessage } from "./FacilityProductsTable";
import { ProductsTableProps } from "./ProductsTable";
import { useIgnoredIssues } from "./Report/useReport";
export const PRODUCTS_WITH_MEASUREMENTS = gql(`
  query ProductsWithMeasurements($facilityId: ID!, $reportingYear: Int!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    productMeasurementsForReport(facilityId: $facilityId, reportingYear: $reportingYear, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        totalMeasurements
        productChemicals {
          chemical {
            name
            ...ChemicalPicker
          }
          maxAmount {
            amount
            unit
          }
       }
       issues {
         ...issue
       }
      }
      count
    }
  }
`);

const DELETE_FACILITY_PRODUCT_MEASUREMENTS_BY_PRODUCT_AND_YEAR = gql(`
  mutation DeleteFacilityProductMeasurementsByProductAndYear($facilityId: ID!, $productId: ID!, $reportingYear: Int!) {
    deleteFacilityProductMeasurementsByProductAndYear(facilityId: $facilityId, productId: $productId, reportingYear: $reportingYear) {
      facilityId
      productId
      reportingYear
    }
  }
`);

type Row = ProductsWithMeasurementsQuery["productMeasurementsForReport"]["items"][number];

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "name", sort: "asc" }];
const EXCLUDE_FILTER_COLUMNS = [
  "issueCount",
  "maxAmount",
  "chemicalsInProduct",
  "facilityProductMeasurementCount",
];
const ADDITIONAL_FILTER_KEYS: FilterKey[] = [{ key: "chemical", header: "Chemical" }];
const getItems = (data: ProductsWithMeasurementsQuery) => data.productMeasurementsForReport.items;
const getCount = (data: ProductsWithMeasurementsQuery) => data.productMeasurementsForReport.count;

type ProductsWithMeasurementsTableProps = ProductsTableProps & {
  usesSearchParams?: boolean;
};

function useProductsWithMeasurementsColumns({
  facilityState,
  ignoredIssues,
  onEdit,
  onDelete,
}: {
  facilityState: string | null | undefined;
  ignoredIssues: ReturnType<typeof useIgnoredIssues>;
  onEdit: (productId: string) => void;
  onDelete: (row: Row) => void;
}) {
  return useMemo<OmnisearchGridColDef<Row>[]>(
    () => [
      {
        headerName: "Product",
        field: "name",
        flex: 2,
        topAligned: true,
        renderCell: ({ row }) => <TextCellWithTooltip>{row.name}</TextCellWithTooltip>,
      },
      {
        headerName: "Measurements",
        field: "facilityProductMeasurementCount",
        flex: 1,
        topAligned: true,
        renderCell: ({ row }) => <TextCellWithTooltip>{row.totalMeasurements}</TextCellWithTooltip>,
      },
      {
        headerName: "Chemicals in Product",
        field: "chemicalsInProduct",
        flex: 2,
        sortable: false,
        topAligned: true,
        renderCell({ row }) {
          if (row.productChemicals?.length) {
            return (
              <Box style={{ width: "100%" }}>
                {row.productChemicals.map(({ chemical }) => (
                  <TextCellWithTooltip key={chemical.id} tooltip={formatChemicalName(chemical)}>
                    {chemical.name}
                  </TextCellWithTooltip>
                ))}
              </Box>
            );
          }
        },
      },
      {
        headerName: "Max Amount",
        field: "maxAmount",
        flex: 1,
        sortable: false,
        topAligned: true,
        renderCell({ row }) {
          if (row.productChemicals?.length) {
            return (
              <Box style={{ width: "100%" }}>
                {row.productChemicals.map(({ chemical, maxAmount }) => (
                  <TextCellWithTooltip key={chemical.id}>
                    {prettyPrintQuantity({
                      amount: maxAmount?.amount,
                      unit: maxAmount?.unit,
                      state: facilityState,
                    })}
                  </TextCellWithTooltip>
                ))}
              </Box>
            );
          }
        },
      },
      {
        headerName: "Issues",
        field: "issueCount",
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const shouldIgnore = ignoredIssues.some((issue) =>
            row.issues?.some((i) => i.modelId === issue.modelId)
          );
          return <IssueCount issueCount={row.issues?.length} ignoreForReport={shouldIgnore} />;
        },
        sortable: false,
        flex: 0.8,
      },
      {
        field: "actions",
        type: "actions",
        getActions({ row }) {
          return [
            <GridActionsCellItem
              key="edit"
              label="Edit"
              icon={<Edit />}
              onClick={() => onEdit(row.id)}
            />,
            <GridActionsCellItem
              key="delete"
              label="Delete"
              icon={<Delete />}
              onClick={() => onDelete(row)}
            />,
          ];
        },
      },
    ],
    [facilityState, ignoredIssues, onEdit, onDelete]
  );
}

export function ProductsWithMeasurementsTable({
  facilityId,
  showReportingYearPicker,
}: ProductsWithMeasurementsTableProps) {
  const ignoredIssues = useIgnoredIssues();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const reportingYear = useSelectedReportingYear();
  const { navigateWithFilters } = useQueryParams<{
    ry: string;
    facilityId: string;
    omnisearch: string;
  }>();

  const { data: facilityQuery } = useFacility(facilityId);
  const facilityState = facilityQuery?.facility?.state;

  const [showAddMeasurementForm, setShowAddMeasurementForm] = useState(false);
  const [deletingProductMeasurements, setDeletingProductMeasurements] = useState<Row | undefined>(
    undefined
  );

  const location = useLocation();
  const usedInInventory = location.pathname.includes("inventory");

  const navigateToProductMeasurements = useCallback(
    (productId: string) => {
      if (usedInInventory) {
        navigateWithFilters(productId, (filters) => {
          filters.facilityId = facilityId;
          filters.ry = String(reportingYear);
        });
        return;
      }

      navigate(productId);
    },
    [navigate, navigateWithFilters, usedInInventory, facilityId, reportingYear]
  );

  const [deleteFacilityProductMeasurementsByProductAndYear, { loading: isDeleting }] = useMutation(
    DELETE_FACILITY_PRODUCT_MEASUREMENTS_BY_PRODUCT_AND_YEAR,
    {
      onCompleted() {
        alerts.success("Successfully deleted the measurements");
        setDeletingProductMeasurements(undefined);
      },
      onError(err) {
        alerts.error("An error occurred deleting the measurements", err);
        setDeletingProductMeasurements(undefined);
      },
      refetchQueries: [PRODUCTS_WITH_MEASUREMENTS],
    }
  );

  const handleDelete = useCallback(() => {
    if (!deletingProductMeasurements) return;
    deleteFacilityProductMeasurementsByProductAndYear({
      variables: {
        facilityId,
        productId: deletingProductMeasurements.id,
        reportingYear,
      },
    });
  }, [
    deleteFacilityProductMeasurementsByProductAndYear,
    deletingProductMeasurements,
    facilityId,
    reportingYear,
  ]);

  const columns = useProductsWithMeasurementsColumns({
    facilityState,
    ignoredIssues,
    onEdit: navigateToProductMeasurements,
    onDelete: setDeletingProductMeasurements,
  });

  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): ProductsWithMeasurementsQueryVariables => {
      const defaults = `facilityId:${facilityId} reportingYear:${reportingYear}`;
      return {
        facilityId,
        reportingYear,
        search: omnisearch ? `${omnisearch} ${defaults}` : defaults,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
      };
    },
    [facilityId, reportingYear]
  );

  const handleAdd = useCallback(() => setShowAddMeasurementForm(true), []);

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<Row>) => navigateToProductMeasurements(row.id),
    [navigateToProductMeasurements]
  );

  const commandButtons = useMemo(
    () => [
      showReportingYearPicker ? (
        <TierIIYearPicker
          key="yearPicker"
          selectProps={{
            size: "small",
          }}
          persistToLocalStorage
        />
      ) : undefined,
      <CommandButton key="add" variant="contained" onClick={handleAdd}>
        Add Product
      </CommandButton>,
    ],
    [showReportingYearPicker, handleAdd]
  );

  return (
    <>
      <OmnisearchDataGridV2
        persistenceKey="products-with-measurements"
        columns={columns}
        query={PRODUCTS_WITH_MEASUREMENTS}
        buildVariables={buildVariables}
        getItems={getItems}
        getCount={getCount}
        initialSortModel={INITIAL_SORT_MODEL}
        initialPageSize={25}
        noRowsMessage="No products found"
        noDataButtonText="Add Product"
        onNoDataButtonClick={handleAdd}
        rowHeight="auto"
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
        commandButtons={commandButtons}
        compact
        onRowClick={handleRowClick}
      />
      {!!deletingProductMeasurements && (
        <ConfirmDialog
          open={!!deletingProductMeasurements}
          onClose={() => setDeletingProductMeasurements(undefined)}
          onConfirm={handleDelete}
          header="Are you sure you want to delete this product measurement?"
          resources={[
            {
              resourceType: "Product Measurement",
              resourceName: deletingProductMeasurements.name,
            },
            {
              resourceType: "Reporting Year",
              resourceName: reportingYear,
            },
          ]}
          info={DeleteProductMessage({
            productName: deletingProductMeasurements.name,
            forMeasurements: true,
          })}
          loading={isDeleting}
        />
      )}
      {showAddMeasurementForm && (
        <FacilityProductMeasurementForm
          facilityId={facilityId}
          open={showAddMeasurementForm}
          onClose={() => setShowAddMeasurementForm(false)}
        />
      )}
    </>
  );
}
